/**
 * Custom gift add-on
 * Palo Alto cart-drawer integration.
 *
 * - Adds the globally selected gift variant with Shopify AJAX.
 * - Uses Palo Alto's native `theme:cart:refresh` event so the drawer
 *   refreshes and opens immediately.
 * - Re-syncs the PDP and cart gift controls after native cart updates,
 *   including line-item removal and quantity changes.
 */
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-custom-gift-add-on]';
  const BUTTON_SELECTOR = '[data-custom-gift-add-on-button]';
  const TEXT_SELECTOR = '[data-custom-gift-add-on-button-text]';
  const STATUS_SELECTOR = '[data-custom-gift-add-on-status]';

  const busyVariants = new Set();

  let latestCart = null;
  let syncTimer = null;

  const parseBoolean = (value) => String(value) === 'true';

  const getRoots = (variantId = null) => {
    const roots = Array.from(document.querySelectorAll(ROOT_SELECTOR));

    if (!variantId) return roots;

    return roots.filter(
      (root) => String(root.dataset.variantId) === String(variantId)
    );
  };

  const getCartUrl = () => {
    const root = document.querySelector(ROOT_SELECTOR);

    if (root?.dataset.cartUrl) {
      return root.dataset.cartUrl;
    }

    const routesRoot =
      window.Shopify?.routes?.root ||
      window.theme?.routes?.root ||
      '/';

    return `${routesRoot}cart.js`;
  };

  const setStatus = (root, message = '') => {
    const status = root.querySelector(STATUS_SELECTOR);

    if (status) {
      status.textContent = message;
    }
  };

  const cartContainsVariant = (cart, variantId) => {
    if (!cart || !Array.isArray(cart.items)) return false;

    return cart.items.some(
      (item) => String(item.variant_id) === String(variantId)
    );
  };

  const updateCartContextVisibility = (root, cart) => {
    if (
      root.dataset.customGiftContext !== 'cart' ||
      !cart ||
      typeof cart.item_count !== 'number'
    ) {
      return;
    }

    const holder =
      root.closest('.custom-gift-add-on-cart-holder') || root;

    holder.classList.toggle('hidden', cart.item_count === 0);
  };

  const updateRootState = (root, cart = latestCart) => {
    const button = root.querySelector(BUTTON_SELECTOR);
    const text = root.querySelector(TEXT_SELECTOR);

    if (!button || !text) return;

    const variantId = root.dataset.variantId;
    const available = parseBoolean(root.dataset.available);
    const preventDuplicates = parseBoolean(
      root.dataset.preventDuplicates
    );

    const inCart = cart
      ? cartContainsVariant(cart, variantId)
      : parseBoolean(root.dataset.inCart);

    updateCartContextVisibility(root, cart);

    root.dataset.inCart = inCart ? 'true' : 'false';
    root.classList.toggle(
      'is-added',
      Boolean(inCart && preventDuplicates)
    );

    button.setAttribute('aria-busy', 'false');

    if (!available) {
      text.textContent = root.dataset.soldOutText || 'Sold out';
      button.disabled = true;
      return;
    }

    if (busyVariants.has(String(variantId))) {
      button.disabled = true;
      return;
    }

    if (inCart && preventDuplicates) {
      const addedText =
        root.dataset.addedText || 'Gift bag added';

      text.textContent = addedText;
      button.disabled = true;
      setStatus(root, addedText);
      return;
    }

    text.textContent =
      root.dataset.buttonText || 'Add gift bag';
    button.disabled = false;
    setStatus(root, '');
  };

  const updateAllStates = (cart = latestCart) => {
    document
      .querySelectorAll(ROOT_SELECTOR)
      .forEach((root) => updateRootState(root, cart));
  };

  const setLoading = (variantId, loading) => {
    getRoots(variantId).forEach((root) => {
      const button = root.querySelector(BUTTON_SELECTOR);

      root.classList.toggle('is-loading', loading);

      if (!button) return;

      button.setAttribute(
        'aria-busy',
        loading ? 'true' : 'false'
      );

      if (loading) {
        button.disabled = true;
      }
    });
  };

  const requestCart = async (cartUrl = getCartUrl()) => {
    const response = await fetch(cartUrl, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`Unable to load cart (${response.status})`);
    }

    return response.json();
  };

  const syncCartState = async () => {
    if (!document.querySelector(ROOT_SELECTOR)) return;

    try {
      const cart = await requestCart();

      latestCart = cart;
      updateAllStates(cart);
    } catch (error) {
      console.warn(
        '[Custom gift add-on] Cart state could not be synchronized.',
        error
      );
    }
  };

  const scheduleCartSync = (delay = 0) => {
    window.clearTimeout(syncTimer);

    syncTimer = window.setTimeout(() => {
      syncCartState();
    }, delay);
  };

  const refreshNativeCart = (cart, sourceElement) => {
    const detail = {
      cart,
      source: 'custom-gift-add-on',
      element: sourceElement
    };

    /*
     * Palo Alto listens for this exact event on <cart-element>.
     * Its native getCart() method refreshes the line items and its
     * build() method opens the drawer after the refreshed markup arrives.
     */
    document.dispatchEvent(
      new CustomEvent('theme:cart:refresh', {
        bubbles: true,
        detail
      })
    );

    window.dispatchEvent(
      new CustomEvent('custom-gift-add-on:added', {
        detail
      })
    );
  };

  const showTemporaryError = (variantId) => {
    getRoots(variantId).forEach((root) => {
      const button = root.querySelector(BUTTON_SELECTOR);
      const text = root.querySelector(TEXT_SELECTOR);
      const message =
        root.dataset.errorText ||
        'Unable to add gift bag. Please try again.';

      setStatus(root, message);

      if (text) {
        text.textContent = message;
      }

      if (button) {
        button.disabled = false;
        button.setAttribute('aria-busy', 'false');
      }
    });

    window.setTimeout(() => {
      updateAllStates(latestCart);
    }, 2200);
  };

  const addGift = async (root) => {
    const variantId = root.dataset.variantId;
    const addUrl = root.dataset.addUrl;
    const cartUrl = root.dataset.cartUrl || getCartUrl();
    const preventDuplicates = parseBoolean(
      root.dataset.preventDuplicates
    );

    if (
      !variantId ||
      !addUrl ||
      !cartUrl ||
      busyVariants.has(String(variantId))
    ) {
      return;
    }

    busyVariants.add(String(variantId));
    setLoading(variantId, true);

    getRoots(variantId).forEach((item) => {
      setStatus(item, '');
    });

    let failed = false;

    try {
      /*
       * Re-check the live cart before adding. This prevents duplicates
       * even if a native cart refresh has not finished visually yet.
       */
      const cartBeforeAdd = await requestCart(cartUrl);

      if (
        preventDuplicates &&
        cartContainsVariant(cartBeforeAdd, variantId)
      ) {
        latestCart = cartBeforeAdd;
        updateAllStates(cartBeforeAdd);
        return;
      }

      const response = await fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1
            }
          ]
        })
      });

      const responseData = await response
        .json()
        .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          responseData.description ||
            responseData.message ||
            'Unable to add gift bag'
        );
      }

      /*
       * Update the PDP control immediately, then ask Palo Alto to
       * refresh and open its native cart drawer.
       */
      latestCart = await requestCart(cartUrl);
      updateAllStates(latestCart);
      refreshNativeCart(latestCart, root);
    } catch (error) {
      failed = true;

      console.error('[Custom gift add-on]', error);
      showTemporaryError(variantId);
    } finally {
      busyVariants.delete(String(variantId));
      setLoading(variantId, false);

      if (!failed) {
        updateAllStates(latestCart);
      }
    }
  };

  const handleNativeCartRender = () => {
    /*
     * A newly loaded drawer contains fresh gift-control markup that was
     * not previously in the live DOM. Apply the latest known state
     * immediately, then verify it against /cart.js.
     *
     * Palo Alto dispatches `theme:cart:added` after every build(),
     * including product additions, quantity changes and removals.
     */
    updateAllStates(latestCart);
    scheduleCartSync();
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest(BUTTON_SELECTOR);

    if (!button) return;

    const root = button.closest(ROOT_SELECTOR);

    if (!root) return;

    event.preventDefault();
    addGift(root);
  });

  document.addEventListener('DOMContentLoaded', () => {
    updateAllStates();
    scheduleCartSync();
  });

  document.addEventListener('shopify:section:load', () => {
    updateAllStates(latestCart);
    scheduleCartSync();
  });

  document.addEventListener('shopify:block:select', () => {
    updateAllStates(latestCart);
    scheduleCartSync();
  });

  document.addEventListener(
    'theme:cart:loaded',
    handleNativeCartRender
  );

  document.addEventListener(
    'theme:cart:added',
    handleNativeCartRender
  );

  window.CustomGiftAddOn = {
    refresh: syncCartState,
    getCart: () => latestCart
  };
})();  